import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import { Users, Plus, Trash2, Pencil, MapPin, Home } from "lucide-react";

const SkupinePage = () => {
  const { token, hasRole } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const canEdit = hasRole(["predsednik", "gospodar", "tajnik", "administrator"]);
  const canEditObjects = hasRole(["predsednik", "gospodar", "tajnik", "administrator", "skupinovodja"]);

  const [groups, setGroups] = useState([]);
  const [objects, setObjects] = useState([]);
  const [types, setTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Group dialog
  const [groupDlg, setGroupDlg] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Object dialog
  const [objDlg, setObjDlg] = useState(false);
  const [editingObj, setEditingObj] = useState(null);

  // Type dialog
  const [typeDlg, setTypeDlg] = useState(false);
  const [newType, setNewType] = useState({ kljuc: "", naziv: "" });
  const [editingType, setEditingType] = useState(null); // { kljuc, naziv, originalKljuc }

  // Member assignment dialog
  const [memberDlg, setMemberDlg] = useState(null); // group object

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, []);

  const fetchAll = async () => {
    try {
      const [g, o, t, m] = await Promise.all([
        axios.get(`${API}/ld-groups`, { headers }),
        axios.get(`${API}/ld-objects`, { headers }),
        axios.get(`${API}/ld-objects/types`, { headers }),
        axios.get(`${API}/users`, { headers }),
      ]);
      setGroups(g.data || []);
      setObjects(o.data || []);
      setTypes(t.data || []);
      setMembers((m.data || []).filter(u => u.aktiven !== false));
    } catch (e) {
      toast.error("Napaka pri nalaganju");
    } finally {
      setLoading(false);
    }
  };

  const saveGroup = async () => {
    if (!editingGroup?.ime?.trim()) { toast.error("Vpiši ime skupine"); return; }
    try {
      const payload = {
        ime: editingGroup.ime.trim(),
        opis: editingGroup.opis || "",
        revir_naziv: editingGroup.revir_naziv || "",
        skupinovodje_ids: editingGroup.skupinovodje_ids || [],
      };
      if (editingGroup.id) {
        await axios.put(`${API}/ld-groups/${editingGroup.id}`, payload, { headers });
        toast.success("Skupina posodobljena");
      } else {
        await axios.post(`${API}/ld-groups`, payload, { headers });
        toast.success("Skupina ustvarjena");
      }
      setGroupDlg(false); setEditingGroup(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`Izbriši skupino "${g.ime}"? Člani ostanejo brez dodelitve.`)) return;
    try {
      await axios.delete(`${API}/ld-groups/${g.id}`, { headers });
      toast.success("Skupina izbrisana");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const saveObject = async () => {
    if (!editingObj?.naziv?.trim() || !editingObj?.tip) {
      toast.error("Vpiši naziv in izberi tip");
      return;
    }
    try {
      const payload = {
        tip: editingObj.tip,
        naziv: editingObj.naziv.trim(),
        opis: editingObj.opis || "",
        skupina_id: editingObj.skupina_id || null,
        lat: editingObj.lat === "" || editingObj.lat === null ? null : Number(editingObj.lat),
        lng: editingObj.lng === "" || editingObj.lng === null ? null : Number(editingObj.lng),
      };
      if (editingObj.id) {
        await axios.put(`${API}/ld-objects/${editingObj.id}`, payload, { headers });
        toast.success("Objekt posodobljen");
      } else {
        await axios.post(`${API}/ld-objects`, payload, { headers });
        toast.success("Objekt dodan");
      }
      setObjDlg(false); setEditingObj(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const deleteObject = async (o) => {
    if (!window.confirm(`Izbriši objekt "${o.naziv}"?`)) return;
    try {
      await axios.delete(`${API}/ld-objects/${o.id}`, { headers });
      toast.success("Izbrisano");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const saveType = async () => {
    // Edit obstoječega custom tipa
    if (editingType) {
      if (!editingType.kljuc?.trim() || !editingType.naziv?.trim()) {
        toast.error("Vpiši ključ in naziv"); return;
      }
      try {
        await axios.put(`${API}/ld-objects/types/${editingType.originalKljuc}`, {
          kljuc: editingType.kljuc.trim().toLowerCase().replace(/\s+/g, "_"),
          naziv: editingType.naziv.trim(),
        }, { headers });
        toast.success("Tip posodobljen");
        setTypeDlg(false); setEditingType(null); setNewType({ kljuc: "", naziv: "" });
        await fetchAll();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Napaka");
      }
      return;
    }
    // Nov tip
    if (!newType.kljuc?.trim() || !newType.naziv?.trim()) {
      toast.error("Vpiši ključ in naziv");
      return;
    }
    try {
      await axios.post(`${API}/ld-objects/types`, {
        kljuc: newType.kljuc.trim().toLowerCase().replace(/\s+/g, "_"),
        naziv: newType.naziv.trim(),
      }, { headers });
      toast.success("Tip dodan");
      setTypeDlg(false); setNewType({ kljuc: "", naziv: "" });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const deleteType = async (t) => {
    if (t.standard) { toast.error("Standardnih tipov ni mogoče brisati"); return; }
    if (!window.confirm(`Izbriši tip "${t.naziv}"?`)) return;
    try {
      await axios.delete(`${API}/ld-objects/types/${t.kljuc}`, { headers });
      toast.success("Izbrisano");
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const assignMembers = async (groupId, memberIds) => {
    try {
      await axios.post(`${API}/ld-groups/${groupId}/members`, { member_ids: memberIds }, { headers });
      toast.success(`Dodeljenih ${memberIds.length} članov`);
      setMemberDlg(null);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const removeMember = async (groupId, memberId) => {
    try {
      await axios.delete(`${API}/ld-groups/${groupId}/members/${memberId}`, { headers });
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Napaka");
    }
  };

  const groupMembers = (groupId) => members.filter(m => m.skupina_id === groupId);
  const groupObjects = (groupId) => objects.filter(o => o.skupina_id === groupId);
  const orphanObjects = objects.filter(o => !o.skupina_id);
  const memberName = (id) => {
    const m = members.find(x => x.id === id);
    return m ? `${m.ime} ${m.priimek}` : "?";
  };
  const typeName = (kljuc) => types.find(t => t.kljuc === kljuc)?.naziv || kljuc;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="skupine-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-3">
            <Users className="h-8 w-8" />
            Skupine in revirji
          </h1>
          <p className="text-muted-foreground mt-1">Upravljanje skupin, revirjev in objektov v loviscu</p>
        </div>
        {(canEdit || canEditObjects) && (
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <Button
                onClick={() => { setEditingGroup({ ime: "", opis: "", revir_naziv: "", skupinovodje_ids: [] }); setGroupDlg(true); }}
                data-testid="add-group-btn"
              >
                <Plus className="h-4 w-4 mr-2" />Dodaj skupino
              </Button>
            )}
            {canEditObjects && (
              <Button
                variant="outline"
                onClick={() => { setEditingObj({ tip: types[0]?.kljuc || "koca", naziv: "", opis: "", skupina_id: "", lat: "", lng: "" }); setObjDlg(true); }}
                data-testid="add-object-btn"
              >
                <Plus className="h-4 w-4 mr-2" />Dodaj objekt
              </Button>
            )}
          </div>
        )}
      </div>

      <Tabs defaultValue="skupine">
        <TabsList>
          <TabsTrigger value="skupine" data-testid="tab-skupine">Skupine ({groups.length})</TabsTrigger>
          <TabsTrigger value="objekti" data-testid="tab-objekti">Objekti ({objects.length})</TabsTrigger>
          <TabsTrigger value="tipi" data-testid="tab-tipi">Tipi objektov</TabsTrigger>
        </TabsList>

        {/* Skupine tab */}
        <TabsContent value="skupine" className="space-y-4 mt-4">
          {groups.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center">
              <Users className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">Še ni ustvarjenih skupin.</p>
              {canEdit && (
                <Button onClick={() => { setEditingGroup({ ime: "", opis: "", revir_naziv: "", skupinovodje_ids: [] }); setGroupDlg(true); }}>
                  <Plus className="h-4 w-4 mr-2" />Dodaj prvo skupino
                </Button>
              )}
            </CardContent></Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {groups.map(g => {
                const gm = groupMembers(g.id);
                const go = groupObjects(g.id);
                return (
                  <Card key={g.id} data-testid={`group-card-${g.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg">{g.ime}</CardTitle>
                          {g.revir_naziv && <CardDescription>Revir: {g.revir_naziv}</CardDescription>}
                          {g.opis && <p className="text-sm text-muted-foreground mt-1">{g.opis}</p>}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1">
                            <Button size="icon" variant="ghost" onClick={() => { setEditingGroup({ ...g }); setGroupDlg(true); }} title="Uredi">
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteGroup(g)} title="Izbriši">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Skupinovodje */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Skupinovodje</p>
                        {g.skupinovodje_ids && g.skupinovodje_ids.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {g.skupinovodje_ids.map(id => (
                              <Badge key={id} variant="secondary">{memberName(id)}</Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Ni dodeljenih</p>
                        )}
                      </div>
                      {/* Članstvo */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Člani ({gm.length})</p>
                          {canEdit && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setMemberDlg(g)} data-testid={`assign-members-${g.id}`}>
                              Uredi člane
                            </Button>
                          )}
                        </div>
                        {gm.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {gm.map(m => (
                              <Badge key={m.id} variant="outline" className="pr-1">
                                {m.ime} {m.priimek}
                                {canEdit && (
                                  <button
                                    className="ml-1 text-muted-foreground hover:text-destructive"
                                    onClick={() => removeMember(g.id, m.id)}
                                    title="Odstrani"
                                  >×</button>
                                )}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Brez članov</p>
                        )}
                      </div>
                      {/* Objekti */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs font-semibold text-muted-foreground uppercase">Objekti ({go.length})</p>
                          {canEditObjects && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setEditingObj({ tip: types[0]?.kljuc || "koca", naziv: "", opis: "", skupina_id: g.id, lat: "", lng: "" });
                                setObjDlg(true);
                              }}
                              data-testid={`add-object-to-group-${g.id}`}
                            >
                              <Plus className="h-3 w-3 mr-1" />Dodaj objekt
                            </Button>
                          )}
                        </div>
                        {go.length > 0 ? (
                          <div className="space-y-1">
                            {go.map(o => (
                              <div key={o.id} className="flex items-center justify-between text-sm bg-muted/40 rounded px-2 py-1">
                                <span className="flex items-center gap-2">
                                  <Home className="h-3 w-3 text-muted-foreground" />
                                  <b>{typeName(o.tip)}</b> – {o.naziv}
                                  {o.lat && o.lng && <MapPin className="h-3 w-3 text-primary" title={`${o.lat}, ${o.lng}`} />}
                                </span>
                                {canEditObjects && (
                                  <div className="flex gap-0.5">
                                    <button className="hover:text-primary" onClick={() => { setEditingObj({ ...o }); setObjDlg(true); }} title="Uredi"><Pencil className="h-3 w-3" /></button>
                                    <button className="hover:text-destructive" onClick={() => deleteObject(o)} title="Izbriši"><Trash2 className="h-3 w-3" /></button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">Brez objektov</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Objekti tab */}
        <TabsContent value="objekti" className="space-y-3 mt-4">
          {objects.length === 0 ? (
            <Card className="border-dashed"><CardContent className="py-10 text-center">
              <Home className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-muted-foreground">Še ni objektov</p>
            </CardContent></Card>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {objects.map(o => {
                const gName = groups.find(x => x.id === o.skupina_id)?.ime || "—";
                return (
                  <Card key={o.id} data-testid={`object-card-${o.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="text-xs">{typeName(o.tip)}</Badge>
                            {o.lat && o.lng && <MapPin className="h-3 w-3 text-primary" />}
                          </div>
                          <p className="font-semibold truncate">{o.naziv}</p>
                          <p className="text-xs text-muted-foreground">Skupina: {gName}</p>
                          {o.opis && <p className="text-xs text-muted-foreground mt-1">{o.opis}</p>}
                          {o.lat && o.lng && <p className="text-[10px] text-muted-foreground mt-1 font-mono">{o.lat.toFixed(5)}, {o.lng.toFixed(5)}</p>}
                        </div>
                        {canEditObjects && (
                          <div className="flex flex-col gap-1">
                            <button className="hover:text-primary" onClick={() => { setEditingObj({ ...o }); setObjDlg(true); }} title="Uredi"><Pencil className="h-3.5 w-3.5" /></button>
                            <button className="hover:text-destructive" onClick={() => deleteObject(o)} title="Izbriši"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
          {orphanObjects.length > 0 && (
            <div className="text-xs text-muted-foreground pt-2">
              {orphanObjects.length} objektov ni dodeljenih nobeni skupini.
            </div>
          )}
        </TabsContent>

        {/* Tipi tab */}
        <TabsContent value="tipi" className="space-y-3 mt-4">
          <div className="flex justify-end">
            {canEditObjects && (
              <Button size="sm" onClick={() => setTypeDlg(true)} data-testid="add-type-btn">
                <Plus className="h-4 w-4 mr-2" />Dodaj tip
              </Button>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {types.map(t => (
              <Card key={t.kljuc}>
                <CardContent className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{t.naziv}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{t.kljuc}</p>
                  </div>
                  {t.standard ? (
                    <Badge variant="secondary" className="text-xs flex-shrink-0">standardni</Badge>
                  ) : canEditObjects ? (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        className="hover:text-primary"
                        onClick={() => {
                          setEditingType({ kljuc: t.kljuc, naziv: t.naziv, originalKljuc: t.kljuc });
                          setTypeDlg(true);
                        }}
                        title="Uredi"
                        data-testid={`edit-type-${t.kljuc}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button className="text-destructive hover:opacity-70" onClick={() => deleteType(t)} title="Izbriši"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Group dialog */}
      <Dialog open={groupDlg} onOpenChange={(o) => { setGroupDlg(o); if (!o) setEditingGroup(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGroup?.id ? "Uredi skupino" : "Nova skupina"}</DialogTitle>
            <DialogDescription>Ime skupine je vidno vsem. Revir je lokalno poimenovanje področja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ime skupine *</Label>
              <Input
                value={editingGroup?.ime || ""}
                onChange={(e) => setEditingGroup({ ...editingGroup, ime: e.target.value })}
                placeholder="npr. 1. skupina"
                data-testid="group-form-name"
                autoFocus
              />
            </div>
            <div>
              <Label>Ime revirja (opcijsko)</Label>
              <Input
                value={editingGroup?.revir_naziv || ""}
                onChange={(e) => setEditingGroup({ ...editingGroup, revir_naziv: e.target.value })}
                placeholder="npr. Pranger, Bukovje"
                data-testid="group-form-revir"
              />
            </div>
            <div>
              <Label>Opis (opcijsko)</Label>
              <Textarea
                rows={2}
                value={editingGroup?.opis || ""}
                onChange={(e) => setEditingGroup({ ...editingGroup, opis: e.target.value })}
                data-testid="group-form-desc"
              />
            </div>
            <div>
              <Label>Skupinovodje</Label>
              <select
                multiple
                value={editingGroup?.skupinovodje_ids || []}
                onChange={(e) => setEditingGroup({ ...editingGroup, skupinovodje_ids: Array.from(e.target.selectedOptions).map(o => o.value) })}
                className="w-full border rounded-md px-2 py-2 h-32 text-sm"
                data-testid="group-form-leaders"
              >
                {members.map(m => (
                  <option key={m.id} value={m.id}>{m.ime} {m.priimek} ({m.vloga})</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground mt-1">Ctrl/Cmd + klik za izbiro več</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupDlg(false)}>Prekliči</Button>
            <Button onClick={saveGroup} data-testid="group-form-save">Shrani</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Object dialog */}
      <Dialog open={objDlg} onOpenChange={(o) => { setObjDlg(o); if (!o) setEditingObj(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingObj?.id ? "Uredi objekt" : "Nov objekt"}</DialogTitle>
            <DialogDescription>Objekti: koče, preže, solnice, kaluže, krmišča, travniki, mrhovišča …</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tip *</Label>
                <select
                  className="w-full border rounded-md px-2 py-2 text-sm"
                  value={editingObj?.tip || ""}
                  onChange={(e) => setEditingObj({ ...editingObj, tip: e.target.value })}
                  data-testid="object-form-type"
                >
                  {types.map(t => (
                    <option key={t.kljuc} value={t.kljuc}>{t.naziv}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Skupina</Label>
                <select
                  className="w-full border rounded-md px-2 py-2 text-sm"
                  value={editingObj?.skupina_id || ""}
                  onChange={(e) => setEditingObj({ ...editingObj, skupina_id: e.target.value })}
                  data-testid="object-form-group"
                >
                  <option value="">— brez —</option>
                  {groups.map(g => (
                    <option key={g.id} value={g.id}>{g.ime}{g.revir_naziv ? ` (${g.revir_naziv})` : ""}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label>Naziv (lokalno ime) *</Label>
              <Input
                value={editingObj?.naziv || ""}
                onChange={(e) => setEditingObj({ ...editingObj, naziv: e.target.value })}
                placeholder="npr. Solnica Bukovje"
                data-testid="object-form-name"
                autoFocus
              />
            </div>
            <div>
              <Label>Opis (opcijsko)</Label>
              <Textarea
                rows={2}
                value={editingObj?.opis || ""}
                onChange={(e) => setEditingObj({ ...editingObj, opis: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>GPS lat (opcijsko)</Label>
                <Input
                  type="number"
                  step="0.00001"
                  value={editingObj?.lat ?? ""}
                  onChange={(e) => setEditingObj({ ...editingObj, lat: e.target.value })}
                  placeholder="45.78123"
                />
              </div>
              <div>
                <Label>GPS lng (opcijsko)</Label>
                <Input
                  type="number"
                  step="0.00001"
                  value={editingObj?.lng ?? ""}
                  onChange={(e) => setEditingObj({ ...editingObj, lng: e.target.value })}
                  placeholder="14.21789"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setObjDlg(false)}>Prekliči</Button>
            <Button onClick={saveObject} data-testid="object-form-save">Shrani</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Type dialog */}
      <Dialog open={typeDlg} onOpenChange={(o) => { setTypeDlg(o); if (!o) { setEditingType(null); setNewType({ kljuc: "", naziv: "" }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingType ? "Uredi tip objekta" : "Nov tip objekta"}</DialogTitle>
            <DialogDescription>Dodajte poljuben tip (npr. "Kopnik", "Vodna korita" …)</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Ključ (interni, samo črke/številke) *</Label>
              <Input
                value={editingType ? editingType.kljuc : newType.kljuc}
                onChange={(e) => editingType
                  ? setEditingType({ ...editingType, kljuc: e.target.value })
                  : setNewType({ ...newType, kljuc: e.target.value })}
                placeholder="kopnik"
                data-testid="type-form-key"
              />
            </div>
            <div>
              <Label>Prikazni naziv *</Label>
              <Input
                value={editingType ? editingType.naziv : newType.naziv}
                onChange={(e) => editingType
                  ? setEditingType({ ...editingType, naziv: e.target.value })
                  : setNewType({ ...newType, naziv: e.target.value })}
                placeholder="Kopnik"
                data-testid="type-form-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDlg(false)}>Prekliči</Button>
            <Button onClick={saveType} data-testid="type-form-save">{editingType ? "Shrani" : "Dodaj"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member assignment dialog */}
      <Dialog open={!!memberDlg} onOpenChange={(o) => { if (!o) setMemberDlg(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Člani skupine – {memberDlg?.ime}</DialogTitle>
            <DialogDescription>Vsak član je lahko samo v eni skupini. Izbira člana ga premakne iz stare skupine.</DialogDescription>
          </DialogHeader>
          <MemberAssignForm
            group={memberDlg}
            allMembers={members}
            onSave={(ids) => assignMembers(memberDlg.id, ids)}
            onCancel={() => setMemberDlg(null)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MemberAssignForm = ({ group, allMembers, onSave, onCancel }) => {
  const [selected, setSelected] = useState(new Set(allMembers.filter(m => m.skupina_id === group?.id).map(m => m.id)));
  const [q, setQ] = useState("");
  if (!group) return null;
  const filtered = allMembers.filter(m => {
    const name = `${m.ime} ${m.priimek}`.toLowerCase();
    return name.includes(q.toLowerCase());
  });
  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };
  return (
    <>
      <Input placeholder="Iskanje ..." value={q} onChange={(e) => setQ(e.target.value)} className="mb-2" />
      <div className="max-h-80 overflow-y-auto border rounded space-y-0.5 p-1">
        {filtered.map(m => (
          <label key={m.id} className="flex items-center gap-2 px-2 py-1 hover:bg-muted/50 rounded cursor-pointer text-sm">
            <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggle(m.id)} />
            <span className="flex-1">{m.ime} {m.priimek}</span>
            {m.skupina_id && m.skupina_id !== group.id && (
              <span className="text-xs text-amber-600">(v drugi skupini)</span>
            )}
          </label>
        ))}
      </div>
      <DialogFooter className="mt-3">
        <Button variant="outline" onClick={onCancel}>Prekliči</Button>
        <Button onClick={() => onSave(Array.from(selected))} data-testid="member-assign-save">
          Shrani ({selected.size})
        </Button>
      </DialogFooter>
    </>
  );
};

export default SkupinePage;
